/**
 * MobilePredictions — the macro-forecaster track record.
 *
 * CLUSTER-4 (research-visibility uplift). The firm tracks named macro predictions (a verbatim quote, a
 * baseline, a by-date) and later evaluates whether each was directionally correct. That track record is
 * credibility — but it lived only on desktop. This screen lists the predictions newest-first, each with
 * its quote, its window (baseline → by-date), and a clear correct / incorrect / pending indicator plus the
 * evaluation notes once they exist.
 *
 * Route: /partner/m/predictions
 *
 * DATA — `mobile_predictions()` SECURITY DEFINER RPC, gated by can_read_research() (raising 42501
 * otherwise). It returns a JSON ARRAY directly:
 *   [{ id, title, quote, baseline, horizon, was_directionally_correct, evaluation_notes, created_at }, …]
 * (LIMIT 60; created_at desc). NB (S2) there are no native title/quote/horizon/baseline columns — `title`
 * is synthesised from organisation/fred_series_id, `quote` is the verbatim_quote, `baseline` is
 * baseline_value, `horizon` is predicted_by_date. `was_directionally_correct` is a tri-state:
 *   true  → Correct,  false → Incorrect,  null → Pending (not yet evaluated).
 *
 * READ-ONLY and offline-aware, mirroring the sibling mobile pages exactly:
 *   • ONE useCachedQuery write-throughs the resolved array (queryFn THROWS on { error } so the cached
 *     last-known-good keeps serving offline rather than being overwritten with a partial/empty value).
 *   • A stable EMPTY array guarantees the screen is never blank on an offline cold start.
 *   • The "Saved <relative time>" header hint + amber-when-offline colour mirror the sibling pages.
 *   • A non-research caller hits the 42501 gate → a graceful no-access card when nothing is cached.
 *
 * The generated supabase types are stale for this RPC, so the call is cast through `any` (matching the
 * other cluster screens). No writes. Web/desktop are untouched (this is a /partner/m/* surface).
 */
import { formatDistanceToNow, format, isValid } from 'date-fns';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, CircleDashed, TrendingUp } from 'lucide-react';
import { ChevronLeft } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Shapes — exactly the keys mobile_predictions() builds.
// ---------------------------------------------------------------------------

interface PredictionRow {
  id: string;
  title: string | null;
  quote: string | null;
  baseline: string | number | null;
  horizon: string | null; // predicted_by_date (date)
  was_directionally_correct: boolean | null;
  evaluation_notes: string | null;
  created_at: string | null;
}

const EMPTY_PREDICTIONS: PredictionRow[] = [];

// Postgres SQLSTATE for insufficient_privilege — the RPC raises this without research read access.
const NO_ACCESS_CODE = '42501';

function errorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

async function fetchPredictions(): Promise<PredictionRow[]> {
  const { data, error } = await (supabase.rpc as any)('mobile_predictions');
  // MUST throw so useCachedQuery keeps the cached placeholder offline and the 42501 state can surface.
  if (error) throw error;
  return Array.isArray(data) ? (data as PredictionRow[]) : [];
}

// ---------------------------------------------------------------------------
// Outcome styling — colour + icon + text (never colour alone), light + dark correct.
// was_directionally_correct: true → Correct, false → Incorrect, null → Pending.
// ---------------------------------------------------------------------------

type Outcome = 'correct' | 'incorrect' | 'pending';

function outcomeOf(v: boolean | null): Outcome {
  if (v === true) return 'correct';
  if (v === false) return 'incorrect';
  return 'pending';
}

interface OutcomeStyle {
  label: string;
  badge: string;
  Icon: typeof CheckCircle2;
  iconClass: string;
}

const OUTCOME_STYLES: Record<Outcome, OutcomeStyle> = {
  correct: {
    label: 'Correct',
    badge: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    Icon: CheckCircle2,
    iconClass: 'text-green-600 dark:text-green-400',
  },
  incorrect: {
    label: 'Incorrect',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    Icon: XCircle,
    iconClass: 'text-red-600 dark:text-red-400',
  },
  pending: {
    label: 'Pending',
    badge: 'bg-muted text-muted-foreground',
    Icon: CircleDashed,
    iconClass: 'text-muted-foreground',
  },
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatRelative(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return null;
  }
}

// predicted_by_date comes as a date string (YYYY-MM-DD); render it as a calendar date, not relative.
function formatByDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (!isValid(d)) return null;
  try {
    return format(d, 'd MMM yyyy');
  } catch {
    return null;
  }
}

function baselineText(value: string | number | null): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s.length > 0 ? s : null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MobilePredictions() {
  const online = useIsOnline();

  const list = useCachedQuery<PredictionRow[]>({
    queryKey: ['mobile-predictions'],
    cacheKey: 'mobile.predictions',
    queryFn: fetchPredictions,
  });

  const items = list.data ?? EMPTY_PREDICTIONS;

  const showStale = list.isFromCache && list.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && list.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(list.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  const noAccess = errorCode(list.error) === NO_ACCESS_CODE;
  const showNoAccess = noAccess && items.length === 0;
  const hardError = !showNoAccess && list.isError && !list.isFromCache && list.data === undefined;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/home"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Home
        </Link>
        <h1 className="font-heading text-2xl font-semibold text-primary">Predictions</h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {items.length > 0 && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              Macro track record
            </p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      <div className="space-y-3">
        {showNoAccess ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <h2 className="font-heading text-sm font-semibold">No access</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              You don’t have access to the prediction track record. This view is available to the research
              team.
            </p>
          </section>
        ) : hardError ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Couldn’t load</p>
            <p className="mt-2 text-sm text-muted-foreground">
              {online
                ? 'We couldn’t load the track record just now. Pull back in a moment.'
                : 'You’re offline and there’s no saved track record yet. This will fill in once you reconnect.'}
            </p>
          </section>
        ) : items.length === 0 ? (
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <TrendingUp className="h-5 w-5" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-sm font-semibold">No predictions yet</h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Tracked macro predictions and how they played out will appear here.
            </p>
            {!online && (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-600">
                You’re offline — this will refresh once you reconnect.
              </p>
            )}
          </section>
        ) : (
          <ul className="space-y-3">
            {items.map((p) => {
              const outcome = outcomeOf(p.was_directionally_correct);
              const style = OUTCOME_STYLES[outcome];
              const { Icon } = style;
              const made = formatRelative(p.created_at);
              const byDate = formatByDate(p.horizon);
              const baseline = baselineText(p.baseline);
              return (
                <li key={p.id}>
                  <section
                    className="rounded-xl border border-border bg-card p-4"
                    aria-label={`Prediction (${style.label}): ${p.title ?? 'Macro prediction'}`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon
                        className={cn('mt-0.5 h-5 w-5 shrink-0', style.iconClass)}
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-2">
                          <h2 className="min-w-0 flex-1 font-heading text-sm font-semibold leading-snug">
                            {p.title ?? 'Macro prediction'}
                          </h2>
                          <span
                            className={cn(
                              'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                              style.badge,
                            )}
                          >
                            {style.label}
                          </span>
                        </div>

                        {/* The prediction itself, as a quote. */}
                        {p.quote && (
                          <blockquote className="mt-2 border-l-2 border-primary/30 pl-3 text-sm italic text-foreground">
                            {p.quote}
                          </blockquote>
                        )}

                        {/* The window: baseline → by-date. */}
                        {(baseline || byDate) && (
                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                            {baseline && <span>Baseline {baseline}</span>}
                            {byDate && <span>By {byDate}</span>}
                          </div>
                        )}

                        {/* Evaluation notes, once the prediction has been judged. */}
                        {p.evaluation_notes && (
                          <p className="mt-2 rounded-lg bg-muted/50 p-2.5 text-sm text-muted-foreground">
                            {p.evaluation_notes}
                          </p>
                        )}

                        {made && (
                          <p className="mt-2 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                            Made {made}
                          </p>
                        )}
                      </div>
                    </div>
                  </section>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
